/**
 * Expert consensus against what the room is actually paying.
 *
 * These are the two numbers a draft turns on, and the only reason to put them
 * side by side is where they disagree. ECR is what a panel of analysts thinks
 * the order should be; ADP is the order real drafts produce. A player the
 * experts rank fortieth and the room takes ninetieth is a player you can wait
 * on. One the experts rank ninetieth and the room takes fortieth is one you
 * will never own at a price you like.
 *
 * The consensus side is read from `public/fantasypros-ecr.json` rather than
 * from the model export, which also carries an `ecrRank`. The export is
 * regenerated by a full model rebuild; the feed is one cheap fetch, and on
 * draft morning the difference between a week-old consensus and this
 * morning's is the whole point of the column — one injury moves a player a
 * hundred slots and flips the sign of his gap.
 */

import { indexPlayers } from './playerMatch.js';

const ECR_JSON = '/fantasypros-ecr.json';

/**
 * Below this the two lists are jittering, not disagreeing.
 *
 * Measured on the 2026 boards over the players both lists carry: the median
 * disagreement inside the top hundred is 5 slots and three quarters of it sits
 * under 11. A threshold under ten would light up half the board and mean
 * nothing on any of it.
 */
export const ADP_GAP_NOISE = 10;

/**
 * A gap this big is a different opinion, not a different rounding.
 *
 * Reserved for the colour that says "look at this row" — in the 2026 boards
 * roughly one player in ten clears it.
 */
export const ADP_GAP_LOUD = 25;

/**
 * Positions the gap is not computed for.
 *
 * Neither list is ordering kickers and defenses against the field. ECR ranks
 * every kicker past 300 because none of them is worth a pick there; ADP puts
 * them in the fourteenth round because you have to roster one. The difference
 * between those two facts is 200 slots of pure artefact, and it would make
 * every kicker on the board the loudest "reach" on it.
 */
export const UNCOMPARED_POSITIONS = new Set(['K', 'DST']);

export const fetchEcrRanks = async () => {
  const response = await fetch(ECR_JSON);
  if (!response.ok) {
    throw new Error(`Could not load consensus ranks (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('Consensus ranks were empty.');
  }
  return payload;
};

let ecrCache = null;

export const getEcrRanksCached = async () => {
  if (ecrCache) return ecrCache;
  ecrCache = await fetchEcrRanks();
  return ecrCache;
};

/**
 * Fold the live consensus onto the board. The feed wins; the model export's
 * own `ecrRank` is the fallback, so a failed fetch costs freshness, not the
 * column.
 */
export const mergeEcrRanks = (board, ecrPlayers) => {
  const lookup = indexPlayers(ecrPlayers || []);

  let matched = 0;
  const merged = board.map((row) => {
    const ecr = lookup(row);
    if (!ecr) return row;
    matched += 1;
    return {
      ...row,
      ecrRank: ecr.ecr ?? row.ecrRank ?? null,
      ecrPosRank: ecr.pos_rank ?? row.ecrPosRank ?? null,
      ecrTier: ecr.tier ?? row.ecrTier ?? null,
      // How far the experts themselves disagreed. A consensus of 40 that
      // ranges 22 to 78 is not the same claim as one that ranges 38 to 43.
      ecrRankMin: ecr.rank_min ?? null,
      ecrRankMax: ecr.rank_max ?? null,
      bye: row.bye ?? ecr.bye ?? null,
    };
  });

  return { board: merged, matched };
};

/**
 * The gap between the two lists, on a scale where they can be subtracted.
 *
 * Their published numbers cannot be. ECR orders nine hundred players and ADP
 * orders the two hundred and thirty somebody actually drafted, so the deeper
 * you go the further ECR's number drifts above ADP's — and subtracting them
 * raw would paint the entire back half of the board as a bargain.
 *
 * Both sides are therefore re-ranked over exactly the players that have both
 * numbers, which removes the drift (the signed median gap comes out at zero on
 * the 2026 boards) and leaves only real disagreement.
 *
 * `adpGap` is in board slots. Positive means the room is drafting him later
 * than the experts rank him, so he falls to you; negative means the room takes
 * him ahead of consensus and you would have to reach.
 */
export const withAdpGap = (board) => {
  const comparable = board.filter(
    (row) =>
      row.ecrRank != null &&
      row.adp != null &&
      !UNCOMPARED_POSITIONS.has(row.position),
  );

  const rankBy = (field) => {
    const byId = new Map();
    [...comparable]
      .sort((a, b) => a[field] - b[field])
      .forEach((row, i) => byId.set(row.id, i + 1));
    return byId;
  };

  const ecrSlots = rankBy('ecrRank');
  const adpSlots = rankBy('adp');

  return board.map((row) => {
    const ecrSlot = ecrSlots.get(row.id) ?? null;
    const adpSlot = adpSlots.get(row.id) ?? null;
    return {
      ...row,
      ecrBoardRank: ecrSlot,
      adpBoardRank: adpSlot,
      adpGap: ecrSlot != null && adpSlot != null ? adpSlot - ecrSlot : null,
    };
  });
};

/**
 * Below this many drafts, an ADP is one room's opinion.
 *
 * Fantasy Football Calculator publishes a pick for anyone who has been taken at
 * all, and a rookie drafted six times can carry an ADP a hundred and fifty
 * slots off consensus purely because those six rooms were the ones that liked
 * him. The gap is still shown — it is real arithmetic on real numbers — but it
 * is not coloured, because the colour is what says "act on this".
 */
export const THIN_ADP_SAMPLE = 25;

/** How loudly to colour a gap: nothing, something, or look-at-this. */
export const gapLevel = (row) => {
  const gap = row?.adpGap;
  if (gap == null || Math.abs(gap) < ADP_GAP_NOISE) return 'quiet';
  if (row.adpTimesDrafted != null && row.adpTimesDrafted < THIN_ADP_SAMPLE) return 'thin';
  const direction = gap > 0 ? 'falls' : 'reach';
  return Math.abs(gap) >= ADP_GAP_LOUD ? `${direction}-loud` : direction;
};

export const formatGapTooltip = (row) => {
  if (row?.adpGap == null) {
    if (UNCOMPARED_POSITIONS.has(row?.position)) {
      return (
        'Not compared for kickers and defenses — the experts rank them past ' +
        '300 and the room drafts them in the fourteenth round, and that gap ' +
        'is an artefact of two different questions, not a disagreement'
      );
    }
    return 'Needs both a consensus rank and an ADP to compare';
  }
  if (Math.abs(row.adpGap) < ADP_GAP_NOISE) {
    return 'The experts and the room agree on him';
  }
  if (row.adpTimesDrafted != null && row.adpTimesDrafted < THIN_ADP_SAMPLE) {
    return (
      `Only ${row.adpTimesDrafted} draft${row.adpTimesDrafted === 1 ? '' : 's'} ` +
      `have taken him, so his ADP is a handful of rooms rather than the market — ` +
      `the ${Math.abs(row.adpGap)}-slot gap is arithmetic, not a signal`
    );
  }
  const slots =
    row.ecrBoardRank != null && row.adpBoardRank != null
      ? `\nOn the players carrying both numbers: consensus has him ` +
        `${row.ecrBoardRank}, the room takes him ${row.adpBoardRank}.`
      : '';

  return row.adpGap > 0
    ? `The room drafts him ${row.adpGap} slots later than the experts rank him — ` +
        `you can wait, and he should still be there.${slots}`
    : `The room takes him ${Math.abs(row.adpGap)} slots earlier than the experts ` +
        `rank him — owning him means paying over consensus.${slots}`;
};

/** Everything behind the ECR cell, including how much the experts fought. */
export const formatEcrTooltip = (row) => {
  if (row?.ecrRank == null) return 'No consensus rank for him';

  const lines = [`Expert consensus rank ${row.ecrRank}`];
  if (row.ecrPosRank != null) lines.push(`${row.position}${row.ecrPosRank} at his position`);
  if (row.ecrTier != null) lines.push(`Tier ${row.ecrTier}`);
  if (row.ecrRankMin != null && row.ecrRankMax != null) {
    lines.push(`The experts themselves ranged ${row.ecrRankMin} to ${row.ecrRankMax}`);
  }
  return lines.join('\n');
};
